import * as Sentry from '@sentry/browser';
import { type ListingItem, StorageHelper } from '@smg-real-estate/hg-search-libs';
import axios from 'axios';
import chunk from 'lodash.chunk';
import { type RouteLocationResolvedGeneric } from 'vue-router';
import { type Commit, Store } from 'vuex';

import { getAuthToken } from '@/helpers/Authentication/authClient';
import {
  bulkDeleteFavouritesListings,
  getFavouritesList,
  removeFavouritesListItem,
  updateFavouritesList,
} from '@/helpers/FavouritesApiClient/ApiClient';
import { StorageNamespace } from '@/helpers/storage-namespaces';
import { isLayoutSolutionActive } from '@/layout-solution/layout-solution';
import { HgLocalStorageKey } from '@/models/HgLocalStorageKey';
import {
  type FavouriteListingDescriptor,
  type FavouritesActions,
  type FavouritesGetters,
  type FavouritesModule,
  FavouritesModuleStatus,
  type FavouritesMutations,
  type FavouritesState,
} from '@/store/types/favourites-state';
import type { FavouritesList, FavouritesListItem } from '@/types';

const storageHelper = new StorageHelper(StorageNamespace.favourites);

// see documentation of batchget operation, which is used in the listings endpoint
// https://docs.aws.amazon.com/amazondynamodb/latest/APIReference/API_BatchGetItem.html
const DYNAMODB_ITEMS_LIMIT = 100;
const ERR_MSG_FETCH_FAVOURITES_LIST_NOT_STORED = 'Favourites list could not be persisted';

function getFavouritesFromLocalStorage() {
  return storageHelper.getLocalStorageItem(HgLocalStorageKey.FAVOURITE_LISTINGS) || [];
}

function mapFavouritesListToDescriptors(
  favouritesList: FavouritesList,
): FavouriteListingDescriptor[] {
  return favouritesList.listItems.map((item) => ({
    listingId: item.itemId,
    text: item.text,
    savedOn: Date.now(),
  }));
}

function commitFavouritesListUpdate(commit: Commit, favouritesList: FavouritesList) {
  commit('setFavouritesList', favouritesList);
  commit('setPersistedNotes', favouritesList.listItems);
  commit('setFavouriteListingDescriptors', mapFavouritesListToDescriptors(favouritesList));
}

export interface FavouritesListItemNoteUpdatePayload extends FavouritesListItem {
  listId: string;
}

export interface PossibleFavouritesListPayload {
  favouritesList?: FavouritesList;
}

export const state = (): FavouritesState => ({
  isFavouritesEnabled: false,
  isUserAuthenticated: false,
  favouritesModuleStatus: FavouritesModuleStatus.UNINITIALIZED,
  isFavouritesOnboardingMessageClosed: false,
  hasUnavailableListings: false,
  favouriteLoadingListingIds: [],
  favouriteListingDescriptors: [],
  unavailableListingIds: [],
  lastRemovedListingDescriptor: null,
  favouriteListingRecords: [],
  favouritesList: null,
  persistedNotes: [],
  features: null,
});

const fetchListingsByIds = async (listingIds: string[]): Promise<ListingItem[]> => {
  try {
    const request = async (ids: string[]) => {
      const idsAsQuery = ids.join(',');
      const { data } = await axios.get<{ listings: ListingItem[] }>(
        `${import.meta.env.VUE_APP_API_BASE_URL}/listings/listings?ids=${idsAsQuery}&sanitize=true`,
      );
      return data.listings;
    };

    const listingChunks = await Promise.all(chunk(listingIds, DYNAMODB_ITEMS_LIMIT).map(request));
    return listingChunks.flat();
  } catch (ex) {
    Sentry.captureException(ex);
    Sentry.captureMessage(`Failed to fetch listings ${listingIds.join(',')} ${ex.response.data}`);
    return [];
  }
};

const getters: FavouritesGetters = {
  isFavouritesInitialized(favouritesState): boolean {
    return favouritesState.favouritesModuleStatus !== FavouritesModuleStatus.UNINITIALIZED;
  },
  isUserAuthenticated(favouritesState): boolean {
    return favouritesState.isUserAuthenticated;
  },
  hasFetchedFavourites(favouritesState): boolean {
    return favouritesState.favouritesModuleStatus === FavouritesModuleStatus.FAVOURITES_FETCHED;
  },
  favouriteListingRecords(favouritesState): ListingItem[] {
    const sortedDescriptors = [...favouritesState.favouriteListingDescriptors].sort(
      (descriptorA, descriptorB) => descriptorB.savedOn - descriptorA.savedOn,
    );

    const records = sortedDescriptors.map((descriptor) =>
      favouritesState.favouriteListingRecords.find(
        (record: ListingItem) => descriptor.listingId === record.listing.id,
      ),
    );

    return records.filter((record) => !!record) as ListingItem[];
  },
  isListingFavourite(favouritesState) {
    return (listingId: string): boolean => {
      return favouritesState.favouriteListingDescriptors.some(
        (descriptor) => descriptor.listingId === listingId,
      );
    };
  },
  isFavouriteLoading(favouritesState) {
    return (listingId: string | null | undefined): boolean => {
      if (!listingId) {
        return false;
      }

      return favouritesState.favouriteLoadingListingIds.includes(listingId);
    };
  },
  unavailableListingsCount(favouritesState) {
    return favouritesState.unavailableListingIds.length;
  },
  unavailableListingsIds(favouritesState) {
    return favouritesState.unavailableListingIds;
  },
  favouritesListId(favouritesState) {
    return favouritesState.favouritesList?.identifier || null;
  },
  persistedNoteByListingId(favouritesState) {
    return (listingId: string) =>
      favouritesState.persistedNotes.find((item) => item.itemId === listingId) || null;
  },
  features(favouritesState) {
    return favouritesState.features;
  },
};

const mutations: FavouritesMutations = {
  setFavouritesModuleStatus: (favouritesState, favouritesModuleStatus: FavouritesModuleStatus) =>
    (favouritesState.favouritesModuleStatus = favouritesModuleStatus),

  setIsFavouritesEnabled: (favouritesState, isFavouritesEnabled: boolean) =>
    (favouritesState.isFavouritesEnabled = isFavouritesEnabled),
  setIsUserAuthenticated: (favouritesState, isUserAuthenticated: boolean) =>
    (favouritesState.isUserAuthenticated = isUserAuthenticated),
  setIsFavouritesOnboardingMessageClosed: (
    favouritesState,
    isFavouritesOnboardingMessageClosed: boolean,
  ) => (favouritesState.isFavouritesOnboardingMessageClosed = isFavouritesOnboardingMessageClosed),
  setHasUnavailableListings: (favouritesState, hasUnavailableListings: boolean) =>
    (favouritesState.hasUnavailableListings = hasUnavailableListings),

  setFavouriteListingDescriptors: (
    favouritesState,
    favouriteListingDescriptors: FavouriteListingDescriptor[],
  ) => (favouritesState.favouriteListingDescriptors = favouriteListingDescriptors),

  addFavouriteLoadingListingId: (favouritesState, listingId: string) => {
    if (favouritesState.favouriteLoadingListingIds.includes(listingId)) {
      return;
    }

    favouritesState.favouriteLoadingListingIds = [
      ...favouritesState.favouriteLoadingListingIds,
      listingId,
    ];
  },

  removeFavouriteLoadingListingId: (favouritesState, listingId: string) => {
    favouritesState.favouriteLoadingListingIds = favouritesState.favouriteLoadingListingIds.filter(
      (loadingListingId) => loadingListingId !== listingId,
    );
  },

  clearFavouriteLoadingListingIds: (favouritesState) => {
    favouritesState.favouriteLoadingListingIds = [];
  },

  setFavouriteListingRecords: (favouritesState, favouriteListingRecords: ListingItem[]) =>
    (favouritesState.favouriteListingRecords = favouriteListingRecords),

  setLastRemovedListingDescriptor: (
    favouritesState,
    favouriteListingDescriptor: FavouriteListingDescriptor | null,
  ) => (favouritesState.lastRemovedListingDescriptor = favouriteListingDescriptor),

  setUnavailableListingIds: (favouritesState, unavailableListingIds: string[]) =>
    (favouritesState.unavailableListingIds = unavailableListingIds),

  setFavouritesList: (favouritesState, favouritesList: FavouritesList | null) =>
    (favouritesState.favouritesList = favouritesList),
  setPersistedNotes: (favouritesState, items: FavouritesListItem[]) => {
    favouritesState.favouritesListItems = items;
    favouritesState.persistedNotes = items;
  },

  setEnabledFeatures: (favouritesState, features) => (favouritesState.features = features),
};

const actions: FavouritesActions = {
  async removeFavourite({ commit, state: favouritesState }, listingId: string): Promise<void> {
    const isAuthenticated = await getAuthToken();

    if (!isAuthenticated) {
      return;
    }

    commit('addFavouriteLoadingListingId', listingId);

    try {
      const favouritesListId = favouritesState.favouritesList?.identifier;
      const userNotes = favouritesState.favouritesList?.listItems.filter(
        (item) => item.itemId === listingId,
      )[0]?.text;
      const favouritesList = await removeFavouritesListItem(favouritesListId, listingId);

      if (!favouritesList) {
        console.warn(ERR_MSG_FETCH_FAVOURITES_LIST_NOT_STORED);
        return;
      }

      const favouriteListingDescriptors = favouritesList.listItems.map((item) => ({
        listingId: item.itemId,
        text: item.text,
        savedOn: Date.now(),
      }));
      commit('setFavouritesList', favouritesList);
      commit('setPersistedNotes', favouritesList.listItems);
      commit('setFavouriteListingDescriptors', favouriteListingDescriptors);
      commit(
        'setFavouriteListingRecords',
        favouritesState.favouriteListingRecords.filter((record) => record.listing.id !== listingId),
      );

      commit('setLastRemovedListingDescriptor', {
        listingId: listingId,
        text: userNotes,
        savedOn: Date.now(),
      });
    } finally {
      commit('removeFavouriteLoadingListingId', listingId);
    }
  },
  // eslint-disable-next-line max-statements
  async fetchFavouriteListingRecords({ commit, state: favouritesState }) {
    // after favourites are loaded, we need to fetch the listings from ListingAPI
    const savedFavouritesCount = favouritesState.favouriteListingDescriptors.length;
    const favouriteListingIds = favouritesState.favouriteListingDescriptors.map(
      (descriptor) => descriptor.listingId,
    );
    let onlineListings: ListingItem[] = [];
    let unavailableListingIds: string[] = [];

    commit('setFavouritesModuleStatus', FavouritesModuleStatus.FETCHING_FAVOURITES);

    if (favouriteListingIds.length > 0) {
      onlineListings = await fetchListingsByIds(favouriteListingIds);
    }

    commit('setFavouritesModuleStatus', FavouritesModuleStatus.FAVOURITES_FETCHED);
    commit('setFavouriteListingRecords', onlineListings);

    if (savedFavouritesCount !== onlineListings.length) {
      const onlineListingIds = onlineListings.map((record: ListingItem) => record.listing.id);

      unavailableListingIds = favouriteListingIds.filter(
        (listingId: string) => !onlineListingIds.includes(listingId),
      );
    }
    commit('setUnavailableListingIds', unavailableListingIds);
    commit('setHasUnavailableListings', unavailableListingIds.length > 0);
  },
  closeOnboardingMessage({ commit }) {
    commit('setIsFavouritesOnboardingMessageClosed', true);
    storageHelper.setLocalStorageItem(HgLocalStorageKey.FAVOURITES_ONBOARDING_MESSAGE_CLOSED, true);
  },
  async restoreLastRemovedFavourite({ commit, dispatch, state: favouritesState }) {
    if (!favouritesState.lastRemovedListingDescriptor) {
      return;
    }

    const listingDescriptor = favouritesState.lastRemovedListingDescriptor;
    const listingId = listingDescriptor.listingId;
    const userNotes = listingDescriptor.text;

    // Fetch favourites from the database
    const serverFavourites = await getFavouritesList();
    const serverListingIds = serverFavourites[0]?.listItems.map((item) => item.itemId) || [];

    // Skip restoring the listing if it's already been restored or saved as a favourite.
    if (serverListingIds.find((serverListingId: string) => serverListingId === listingId)) {
      return;
    }

    const favouritesListId = favouritesState.favouritesList?.identifier;
    const updatedFavouriteListing = await updateFavouritesList(
      favouritesListId,
      listingId,
      userNotes,
    );

    if (updatedFavouriteListing) {
      const favouriteListingDescriptors = updatedFavouriteListing.listItems.map((item) => ({
        listingId: item.itemId,
        text: item.text,
        savedOn: Date.now(),
      }));
      commit('setFavouritesList', updatedFavouriteListing);
      commit('setPersistedNotes', updatedFavouriteListing.listItems);
      commit('setFavouriteListingDescriptors', favouriteListingDescriptors);
      commit('setLastRemovedListingDescriptor', null);
      await dispatch('fetchFavouriteListingRecords');
    } else {
      console.warn(ERR_MSG_FETCH_FAVOURITES_LIST_NOT_STORED);
    }
  },
  async removeUnavailableFavourites({ commit, state: favouritesState }) {
    const authToken = await getAuthToken();

    let favouriteListingDescriptors;
    if (authToken) {
      favouriteListingDescriptors = favouritesState.favouriteListingDescriptors;
    } else {
      favouriteListingDescriptors = getFavouritesFromLocalStorage();
    }

    const updatedFavouriteListingDescriptors = favouriteListingDescriptors.filter(
      (descriptor: FavouriteListingDescriptor) =>
        !favouritesState.unavailableListingIds.includes(descriptor.listingId),
    );

    commit('setFavouriteListingDescriptors', updatedFavouriteListingDescriptors);
    if (!authToken) {
      commit('setUnavailableListingIds', []);
      storageHelper.setLocalStorageItem(
        HgLocalStorageKey.FAVOURITE_LISTINGS,
        updatedFavouriteListingDescriptors,
      );
    }
  },

  async fetchFavouriteList({ commit }) {
    const [favouritesList] = await getFavouritesList();
    const list = favouritesList ?? null;
    commit('setFavouritesList', list);
    return list;
  },

  async fetchFavouriteListingDescriptors({ commit, state: favouritesState }) {
    const favouritesList = favouritesState.favouritesList;

    if (favouritesList) {
      const favouriteListingDescriptors = favouritesList.listItems.map((item) => ({
        listingId: item.itemId,
        text: item.text,
        savedOn: Date.now(),
      }));
      commit('setFavouriteListingDescriptors', favouriteListingDescriptors);
    } else {
      console.warn('Fetched favourites list is empty or undefined');
    }
  },
  async persistNoteForAuthUser(
    { commit, state: favouritesState },
    payload: FavouritesListItemNoteUpdatePayload,
  ) {
    const listId = payload.listId || favouritesState.favouritesList?.identifier;
    const { itemId, text } = payload;
    const favouritesList = await updateFavouritesList(listId, itemId, text);

    if (favouritesList) {
      const favouriteListingDescriptors = favouritesList.listItems.map((item) => ({
        listingId: item.itemId,
        text: item.text,
        savedOn: Date.now(),
      }));
      commit('setFavouriteListingDescriptors', favouriteListingDescriptors);
      commit('setPersistedNotes', favouritesList.listItems);
      commit('setFavouritesList', favouritesList);
    } else {
      console.warn(ERR_MSG_FETCH_FAVOURITES_LIST_NOT_STORED);
    }
  },
  async dismissUnavailableListingsBanner({ commit, dispatch, state: favouritesState }) {
    const { unavailableListingIds } = favouritesState;

    if (unavailableListingIds.length === 0) {
      return false;
    }

    const authToken = await getAuthToken();

    if (!authToken) {
      await dispatch('removeUnavailableFavourites');
      commit('setHasUnavailableListings', false);
      return true;
    }

    const listId = favouritesState.favouritesList?.identifier;

    if (!listId) {
      return false;
    }

    const updatedFavouritesList = await bulkDeleteFavouritesListings(listId, unavailableListingIds);

    if (!updatedFavouritesList) {
      console.warn(ERR_MSG_FETCH_FAVOURITES_LIST_NOT_STORED);
      return false;
    }

    commitFavouritesListUpdate(commit, updatedFavouritesList);
    commit('setUnavailableListingIds', []);
    commit('setHasUnavailableListings', false);
    return true;
  },
};

// eslint-disable-next-line max-statements
export async function initializeFavouritesStore(
  store: Store<{ favourites: FavouritesState }>,
  route: RouteLocationResolvedGeneric,
): Promise<void> {
  let isFavouritesEnabled = false;
  let isFavouritesOnboardingMessageClosed = false;
  let persistedFavouriteListingDescriptors: FavouriteListingDescriptor[] = [];
  let persistedNotes: FavouritesListItem[] = [];
  let isUserAuthenticated = false;

  try {
    isFavouritesEnabled = !isLayoutSolutionActive(route.query);

    // Check if user is authenticated
    const authToken = await getAuthToken();
    isUserAuthenticated = !!authToken;
    if (isUserAuthenticated) {
      // Populate the store with favourites fetched from the server
      const favouritesList = await store.dispatch('fetchFavouriteList');
      await store.dispatch('fetchFavouriteListingDescriptors');

      isFavouritesOnboardingMessageClosed = false;
      persistedFavouriteListingDescriptors = store.state.favourites.favouriteListingDescriptors;
      persistedNotes = favouritesList?.listItems || [];
    }
  } catch (e) {
    console.error('Error during initialization:', e);
  }

  // Ensure only valid data is committed
  if (Array.isArray(persistedFavouriteListingDescriptors)) {
    store.commit('setFavouriteListingDescriptors', persistedFavouriteListingDescriptors);
  } else {
    console.warn(
      'Invalid persistedFavouriteListingDescriptors:',
      persistedFavouriteListingDescriptors,
    );
  }

  store.commit('setFavouritesModuleStatus', FavouritesModuleStatus.INITIALIZED);
  store.commit('setIsUserAuthenticated', isUserAuthenticated);
  store.commit('setIsFavouritesEnabled', isFavouritesEnabled);
  store.commit('setIsFavouritesOnboardingMessageClosed', isFavouritesOnboardingMessageClosed);
  store.commit('clearFavouriteLoadingListingIds');
  store.commit('setFavouriteListingDescriptors', persistedFavouriteListingDescriptors);
  store.commit('setPersistedNotes', persistedNotes);
}

export default { state, getters, mutations, actions } as FavouritesModule;
